import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

/* ───────── BottomSheet ─────────
   Render trong phần tử có position:relative (khung app). Esc / bấm nền để đóng; khoá focus cơ bản. */
export function BottomSheet({ open, onClose, children, label, dismissible = true, handle = true, tall = false }: { open: boolean; onClose: () => void; children: ReactNode; label: string; dismissible?: boolean; handle?: boolean; tall?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose; // onClose thường là hàm inline mới mỗi lần render – không đưa vào deps
  useEffect(() => {
    if (!open) return;
    const prev = document.activeElement as HTMLElement | null;
    if (!ref.current?.contains(document.activeElement)) ref.current?.focus();
    const focusables = () => Array.from(ref.current?.querySelectorAll<HTMLElement>('a[href],button:not([disabled]),input:not([disabled]),textarea:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])') ?? []);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && dismissible) return closeRef.current();
      if (e.key !== 'Tab') return;
      const list = focusables();
      if (list.length === 0) { e.preventDefault(); ref.current?.focus(); return; }
      const first = list[0]!, last = list[list.length - 1]!;
      const active = document.activeElement as HTMLElement | null;
      if (!ref.current?.contains(active)) { e.preventDefault(); first.focus(); }
      else if (e.shiftKey && (active === first || active === ref.current)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); prev?.focus?.(); };
  }, [open, dismissible]);
  if (!open) return null;
  return (
    <>
      <div className="g-backdrop" onClick={dismissible ? onClose : undefined} aria-hidden="true" />
      <div ref={ref} tabIndex={-1} className={`g-sheet${handle ? '' : ' g-sheet--nohandle'}${tall ? ' g-sheet--tall' : ''}`} role="dialog" aria-modal="true" aria-label={label}>
        {handle && <div className="g-sheet__grab" />}
        {children}
      </div>
    </>
  );
}

/* ───────── Toast ───────── */
interface ToastItem { id: number; message: string }
const ToastCtx = createContext<(message: string) => void>(() => undefined);
export const useToast = () => useContext(ToastCtx);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const seq = useRef(0);
  const push = useCallback((message: string) => {
    const id = ++seq.current;
    setItems((x) => [...x.slice(-2), { id, message }]);
    setTimeout(() => setItems((x) => x.filter((t) => t.id !== id)), 3200);
  }, []);
  const value = useMemo(() => push, [push]);
  return (
    <ToastCtx.Provider value={value}>
      {children}
      <div className="g-toast-region" role="status" aria-live="polite">
        {items.map((t) => <div key={t.id} className="g-toast">{t.message}</div>)}
      </div>
    </ToastCtx.Provider>
  );
}
